import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token management
const getToken = () => localStorage.getItem('token');
const setToken = (token) => localStorage.setItem('token', token);
const removeToken = () => localStorage.removeItem('token');

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses with retry logic
let isRefreshing = false;
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshing) {
      originalRequest._retry = true;
      const token = getToken();
      if (token) {
        // Token exists but request failed - retry once with fresh token
        console.log('[API] Retrying request with existing token');
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }
      // No token - redirect to login
      console.log('[API] No token found, redirecting to login');
      removeToken();
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============== AUTH APIs ==============

// Magic Code Authentication
export const initiateAuth = async (email, name = null) => {
  const response = await api.post('/auth/initiate', { email, name });
  return response.data;
};

export const verifyAuth = async (email, code) => {
  const response = await api.post('/auth/verify', { email, code });
  if (response.data.access_token) {
    setToken(response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const resendCode = async (email, name = null) => {
  const response = await api.post('/auth/resend-code', { email, name });
  return response.data;
};

// Google OAuth
export const googleAuthCallback = async (sessionId) => {
  const response = await api.post('/auth/google/callback', { session_id: sessionId });
  if (response.data.access_token) {
    setToken(response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

// Legacy password auth (kept for backward compatibility)
export const register = async (email, password, name, company = null) => {
  const response = await api.post('/auth/register', { email, password, name, company });
  if (response.data.access_token) {
    setToken(response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  if (response.data.access_token) {
    setToken(response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const logout = () => {
  removeToken();
  localStorage.removeItem('user');
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const updateProfile = async (data) => {
  const response = await api.put('/auth/me', data);
  if (response.data.user) {
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

export const uploadBotAvatar = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/auth/upload-bot-avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const deleteBotAvatar = async () => {
  const response = await api.delete('/auth/delete-bot-avatar');
  return response.data;
};

export const isAuthenticated = () => {
  return !!getToken();
};

export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// ============== Meeting APIs ==============
export const getMeetings = async (page = 1, perPage = 5, status = null, search = null) => {
  let url = `/meetings?page=${page}&per_page=${perPage}`;
  if (status) url += `&status=${status}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  const response = await api.get(url);
  return response.data;
};

export const getMeeting = async (meetingId) => {
  const response = await api.get(`/meetings/${meetingId}`);
  return response.data;
};

export const createMeeting = async (data) => {
  const response = await api.post('/meetings', data);
  return response.data;
};

export const deleteMeeting = async (meetingId) => {
  const response = await api.delete(`/meetings/${meetingId}`);
  return response.data;
};

// ============== Bot APIs ==============
export const joinMeetingWithBot = async (meetingId) => {
  const response = await api.post(`/meetings/${meetingId}/bot/join`);
  return response.data;
};

export const leaveMeetingWithBot = async (meetingId) => {
  const response = await api.post(`/meetings/${meetingId}/bot/leave`);
  return response.data;
};

export const getBotStatus = async (meetingId) => {
  const response = await api.get(`/meetings/${meetingId}/bot/status`);
  return response.data;
};

export const syncBotStatus = async (meetingId) => {
  const response = await api.post(`/meetings/${meetingId}/bot/sync`);
  return response.data;
};

export const getLiveTranscript = async (meetingId) => {
  const response = await api.get(`/meetings/${meetingId}/live-transcript`);
  return response.data;
};

// ============== Upload API ==============
export const uploadMeetingFile = async (file, title, platform = 'upload') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  formData.append('platform', platform);
  
  const response = await api.post('/meetings/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// ============== Transcript APIs ==============
export const getTranscript = async (meetingId) => {
  const response = await api.get(`/meetings/${meetingId}/transcript`);
  return response.data;
};

export const searchTranscript = async (meetingId, query) => {
  const response = await api.get(`/meetings/${meetingId}/transcript/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

// ============== Analysis APIs ==============
export const getAnalysis = async (meetingId) => {
  const response = await api.get(`/meetings/${meetingId}/analysis`);
  return response.data;
};

export const rerunAnalysis = async (meetingId) => {
  const response = await api.post(`/meetings/${meetingId}/analysis/rerun`);
  return response.data;
};

// ============== MoM APIs ==============
export const getMom = async (meetingId) => {
  const response = await api.get(`/meetings/${meetingId}/mom`);
  return response.data;
};

export const updateMom = async (meetingId, content) => {
  const response = await api.put(`/meetings/${meetingId}/mom`, content);
  return response.data;
};

export const regenerateMom = async (meetingId, template = 'enterprise_formal') => {
  const response = await api.post(`/meetings/${meetingId}/mom/regenerate?template=${template}`);
  return response.data;
};

export const exportMom = async (meetingId, format = 'pdf') => {
  // GET request for binary file download
  const response = await api.get(`/meetings/${meetingId}/mom/export?format=${format}`, {
    responseType: 'blob'
  });
  return response.data;
};

export const sendMomEmail = async (meetingId, recipients, subject = null, message = null, includePdf = false) => {
  const response = await api.post(`/meetings/${meetingId}/mom/email`, {
    recipients,
    subject,
    message,
    include_pdf: includePdf
  });
  return response.data;
};

export const assignActionTask = async (meetingId, actionIndex, assignedTo, assignedToEmail = null, status = null, notes = null) => {
  const response = await api.post(`/meetings/${meetingId}/actions/assign`, {
    action_index: actionIndex,
    assigned_to: assignedTo,
    assigned_to_email: assignedToEmail,
    status,
    notes
  });
  return response.data;
};

// ============== Task APIs ==============
export const getTasks = async (status = null, priority = null, tag = null) => {
  let url = '/tasks?';
  if (status) url += `status=${status}&`;
  if (priority) url += `priority=${priority}&`;
  if (tag) url += `tag=${encodeURIComponent(tag)}&`;
  const response = await api.get(url);
  return response.data;
};

export const getTask = async (taskId) => {
  const response = await api.get(`/tasks/${taskId}`);
  return response.data;
};

export const createTask = async (taskData) => {
  const response = await api.post('/tasks', taskData);
  return response.data;
};

export const updateTask = async (taskId, updates) => {
  const response = await api.patch(`/tasks/${taskId}`, updates);
  return response.data;
};

export const deleteTask = async (taskId) => {
  const response = await api.delete(`/tasks/${taskId}`);
  return response.data;
};

export const createTaskFromAction = async (meetingId, actionIndex) => {
  const response = await api.post(`/tasks/from-action/${meetingId}/${actionIndex}`);
  return response.data;
};

// ============== Analytics APIs ==============
export const getAnalyticsOverview = async () => {
  const response = await api.get('/analytics/overview');
  return response.data;
};

export const getAnalyticsTrends = async () => {
  const response = await api.get('/analytics/trends');
  return response.data;
};

export const getSpeakerAnalytics = async () => {
  const response = await api.get('/analytics/speakers');
  return response.data;
};

export const getDecisionsActionsAnalytics = async () => {
  const response = await api.get('/analytics/decisions-actions');
  return response.data;
};

// ============== URL Validation ==============
export const validateMeetingUrl = async (url) => {
  const response = await api.post('/validate-meeting-url', { url });
  return response.data;
};

// ============== Demo API ==============
export const createSampleMeeting = async () => {
  const response = await api.post('/demo/create-sample-meeting');
  return response.data;
};

export default api;
